/**
 * Team chat (ADR 0029): a short thread beside the board. Messages are posted over HTTP;
 * the socket brings everyone else's in. The core owns this panel entirely — a puzzle type
 * never sees a chat line.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardTitle, Input, useToast } from "@/core/ui";
import { useSession } from "@/core/auth/useSession";
import { useTopic } from "@/core/realtime/RealtimeProvider";
import { teamTopic } from "@/core/realtime/protocol";
import { appendChatMessage, useChatMessages, useSendChatMessage } from "./api";

const MAX_LENGTH = 500;

export function ChatPanel({ teamId }: { teamId: number }) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const session = useSession();
  const messages = useChatMessages(teamId);
  const send = useSendChatMessage(teamId);
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLOListElement>(null);
  const myId = session.data?.user?.id ?? null;

  useTopic(teamTopic(teamId), (frame) => {
    if (frame.type === "chat.message") appendChatMessage(qc, teamId, frame.data);
  });

  const count = messages.data?.results.length ?? 0;
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [count]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || send.isPending) return;
    send.mutate(body, {
      onSuccess: () => setDraft(""),
      onError: () => toast(t("chat.send_failed"), "danger"),
    });
  };

  const clock = new Intl.DateTimeFormat(i18n.language, { hour: "2-digit", minute: "2-digit" });
  const inputId = `chat-input-${teamId}`;

  return (
    <Card className="bt-card--panel bt-chat">
      <CardTitle>{t("chat.title")}</CardTitle>
      <ol ref={listRef} className="bt-chat__list" aria-live="polite" aria-relevant="additions">
        {count === 0 ? <li className="bt-chat__empty">{t("chat.empty")}</li> : null}
        {(messages.data?.results ?? []).map((message) => {
          const mine = message.author !== null && message.author.id === myId;
          return (
            <li
              key={message.id}
              className={mine ? "bt-chat__message bt-chat__message--mine" : "bt-chat__message"}
            >
              <div className="bt-chat__meta">
                <span className="bt-chat__author">
                  {message.author?.public_name ?? t("chat.anonymous")}
                </span>
                <time className="bt-chat__time" dateTime={message.created_at}>
                  {clock.format(new Date(message.created_at))}
                </time>
              </div>
              <p className="bt-chat__body">{message.body}</p>
            </li>
          );
        })}
      </ol>
      <form className="bt-chat__form" onSubmit={submit}>
        <label htmlFor={inputId} className="bt-chat__label">
          {t("chat.placeholder")}
        </label>
        <Input
          id={inputId}
          value={draft}
          maxLength={MAX_LENGTH}
          autoComplete="off"
          placeholder={t("chat.placeholder")}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button type="submit" variant="secondary" loading={send.isPending} disabled={!draft.trim()}>
          {t("chat.send")}
        </Button>
      </form>
    </Card>
  );
}
